import React, { useState } from 'react';
import Modal from '../Modal/Modal';
import ShareModal from '../ShareModal/ShareModal';
import RewardedTasksModal from '../RewardedTasksModal/RewardedTasksModal';
import styles from './EarnTokenModal.module.css';
import { useMetadata } from '../../context/MetadataContext';
import { useGameState } from '../../context/GameStateContext';
import { useEarnTokens } from '../../hooks/useEarnTokens';

const EarnTokenModal = ({ isOpen, onClose }) => {
  const { getText } = useMetadata();
  const { tokenBalance } = useGameState();
  const {
    unclaimedBalance,
    isLoading,
    isClaiming,
    isBuying,
    isInviting,
    isLoadingTasks,
    claimTokens,
    createCheckout,
    fetchInviteCode,
    fetchRewardedTasksUrl,
  } = useEarnTokens(isOpen);
  const [showShareModal, setShowShareModal] = useState(false);
  const [inviteUrl, setInviteUrl] = useState('');
  const [showRewardedTasksModal, setShowRewardedTasksModal] = useState(false);
  const [rewardedTasksUrl, setRewardedTasksUrl] = useState('');

  const handleClaimTokens = claimTokens;

  const handleBuyTokens = async () => {
    const checkoutUrl = await createCheckout();

    // Redirect to Stripe checkout
    if (checkoutUrl) {
      window.location.href = checkoutUrl;
    }
  };

  const handleInviteFriends = async () => {
    const inviteCode = await fetchInviteCode();

    // Construct invite URL
    if (inviteCode) {
      const url = `${window.location.origin}?invite_code=${inviteCode}`;
      setInviteUrl(url);
      setShowShareModal(true);
    }
  };

  const handleRewardedTasks = async () => {
    const url = await fetchRewardedTasksUrl();

    // Get the offerwall URL from backend
    if (url) {
      setRewardedTasksUrl(url);
      setShowRewardedTasksModal(true);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={getText('tokens.earn_title')}
      className={styles.earnTokenModal}
      backdropClassName={styles.highZIndex}
      closeOnBackdropClick={false}
    >
      <div className={styles.earnTokenContent}>
        <div className={styles.balanceSection}>
          <div className={styles.balanceGrid}>
            <div className={styles.balanceItem}>
              <div className={styles.balanceLabel}>{getText('common_text.token_balance')}</div>
              <div className={styles.balanceValue}>
                {tokenBalance || '0'}
              </div>
            </div>
            <div className={styles.balanceItem}>
              <div className={styles.balanceLabel}>{getText('tokens.unclaimed_label')}</div>
              <div className={styles.balanceValue}>
                {isLoading ? '...' : (
                  unclaimedBalance ? (
                    <button 
                      className={styles.claimLink}
                      onClick={handleClaimTokens}
                      disabled={isClaiming}
                    >
                      {isClaiming ? '...' : unclaimedBalance}
                    </button>
                  ) : '0'
                )}
              </div>
            </div>
          </div>
          <div className={styles.balanceDescription}>
            {getText('tokens.earn_description')}
          </div>
        </div>
        
        <div className={styles.optionsGrid}>
          {/* Watch Ads option - temporarily hidden until AdSense approval */}
          {/* <div className={styles.option}>
            <div className={styles.optionIcon}>📺</div>
            <div className={styles.optionContent}>
              <h4>{getText('tokens.ads_title')}</h4>
              <p>{getText('tokens.ads_description')}</p>
            </div>
          </div> */}

          <button
            className={styles.option}
            onClick={handleRewardedTasks}
            disabled={isLoadingTasks}
          >
            <div className={styles.optionIcon}>🎯</div>
            <div className={styles.optionContent}>
              <h4>{getText('tokens.rewarded_task_title')}</h4>
              <p>{isLoadingTasks ? 'Loading...' : (getText('tokens.rewarded_task_description'))}</p>
            </div>
          </button>

          <button
            className={styles.option}
            onClick={handleBuyTokens}
            disabled={isBuying}
          >
            <div className={styles.optionIcon}>💳</div>
            <div className={styles.optionContent}>
              <h4>{getText('tokens.buy_title')}</h4>
              <p>{isBuying ? getText('tokens.buy_loading') || 'Loading...' : getText('tokens.buy_description')}</p>
            </div>
          </button>
          
          <button
            className={styles.option}
            onClick={handleInviteFriends}
            disabled={isInviting}
          >
            <div className={styles.optionIcon}>👥</div>
            <div className={styles.optionContent}>
              <h4>{getText('tokens.invite_title')}</h4>
              <p>{isInviting ? 'Loading...' : getText('tokens.invite_description')}</p>
            </div>
          </button>
          
          <div className={styles.option}>
            <div className={styles.optionIcon}>🧩</div>
            <div className={styles.optionContent}>
              <h4>{getText('tokens.create_title')}</h4>
              <p>{getText('tokens.create_description')}</p>
            </div>
          </div>
        </div>
        
        <div className={styles.footerSection}>
          <h4>{getText('tokens.premium_title')}</h4>
          <p className={styles.footerText}>
            {getText('tokens.premium_description')}
          </p>
        </div>
      </div>
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        shareUrl={inviteUrl}
        modalType="invite"
      />
      <RewardedTasksModal
        isOpen={showRewardedTasksModal}
        onClose={() => setShowRewardedTasksModal(false)}
        offerwallUrl={rewardedTasksUrl}
      />
    </Modal>
  );
};

export default EarnTokenModal;